import React, { useState, useEffect } from "react";
import Layout from "../../../components/Layout";
import { get, post } from "../../../services/ApiService";
import "../../custom-style.css";
import Loader from "../../../components/Loader";
import toast, { Toaster } from "react-hot-toast";
import Breadcrumbs from "../../../components/Breadcrumbs";
import TextArea from "antd/es/input/TextArea";
import { useParams, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const IncidentLogEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isNotFound, setNotFound] = useState(false);
  const [showOtherType, setShowOtherType] = useState(false);

  const incidentTypes = [
    "Wrong drug dispensed",
    "Wrong strength dispensed",
    "Wrong drug form dispensed",
    "Incorrect quantity dispensed",
    "Expired medication dispensed",
    "Medication dispensed to wrong patient",
    "Incorrect dosage instructions",
    "Allergic reaction to medication",
    "Drug interaction issue",
    "Dispensing without prescription",
    "Labeling error",
    "Patient received wrong medication",
    "Near miss that reached patient",
    "Other",
  ];

  const possibleCauses = [
    "Staffing levels",
    "Workload",
    "Inattention or distraction",
    "Miscommunication",
    "Look alike drug",
    "Sound alike drug",
    "Label description",
    "Stock mix-ups",
    "Expired or recalled medications",
    "Environmental factors (noise, temperature)",
    "Lack of accuracy checks",
    "Inadequate SOP",
    "Inadequate training",
    "Other",
  ];

  const [formData, setFormData] = useState({
    date: "",
    time: "",
    staffingLevel: "",
    despensedBy: "",
    checkedBy: "",
    drugName: "",
    drugBrand: "",
    drugStrength: "",
    drugForm: "",
    type: "",
    otherType: "",
    cause: "",
    things: "",
    action: "",
    discussion: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Show/hide other type textarea based on selection
    if (name === "type") {
      setShowOtherType(value === "Other");
    }
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      date: date,
    });
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setIsDataLoading(true);
      const res = await get(`api/external-log/incident-log/${id}`);
      console.log(res);

      if (res.success) {
        const data = res.nearMissLog;
        setFormData({
          date: data?.date,
          time: data?.time,
          staffingLevel: data?.staffingLevel,
          despensedBy: data?.despensedBy,
          checkedBy: data?.checkedBy,
          drugName: data?.drugName,
          drugBrand: data?.drugBrand || "",
          drugStrength: data?.drugStrength || "",
          drugForm: data?.drugForm || "",
          type: data?.type,
          otherType: data?.otherType || "",
          cause: data?.cause,
          things: data?.things,
          action: data?.action,
          discussion: data?.discussion,
        });

        // Show other type field if type is "Other"
        setShowOtherType(data?.type === "Other");
      } else {
        setNotFound(true);
      }
    } catch (error) {
      setNotFound(true);
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      const res = await post(
        `api/external-log/incident-log/update/${id}`,
        formData
      );
      console.log(res);
      if (res.success) {
        toast.success(res.message);
        setIsLoading(false);
        navigate("/incident-logs");
      } else {
        toast.error(res.error);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
      toast.error("An error occurred. Please try again.");
    }
  };

  const handleThingsChange = (things) => {
    setFormData({
      ...formData,
      things: things,
    });
  };

  const handleActionChange = (action) => {
    setFormData({
      ...formData,
      action: action,
    });
  };

  const handleDiscussionChange = (discussion) => {
    setFormData({
      ...formData,
      discussion: discussion,
    });
  };

  return (
    <>
      <Layout>
        <div>
          <Toaster position="top-center" reverseOrder={false} />
          <div className="row mb-9 align-items-center">
            <div className="col-xxl-9">
              <div className="row">
                <div className="col-sm-6 mb-8 mb-sm-0">
                  <h2 className="fs-4 mb-0">Edit Incident Log</h2>
                  <Breadcrumbs />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xxl-9">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card mb-8 rounded-4">
                    <div className="card-body p-7">
                      {isDataLoading && (
                        <div className="text-center">
                          <Loader />
                        </div>
                      )}
                      {!isDataLoading && !isNotFound && (
                        <form className="form-border-1" onSubmit={handleSubmit}>
                          <div className="row">
                            <div className="col-lg-6 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="product_title"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Date
                                </label>
                                <DatePicker
                                  selected={formData.date}
                                  onChange={handleDateChange}
                                  dateFormat="dd/MM/yyyy"
                                  className="form-control"
                                  placeholderText="DD/MM/YYYY"
                                  required
                                />
                              </div>
                            </div>
                            <div className="col-lg-6 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="product_title"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Time
                                </label>
                                <input
                                  type="text"
                                  name="time"
                                  placeholder="Type here"
                                  className="form-control"
                                  value={formData.time}
                                  onChange={handleChange}
                                  required
                                />
                              </div>
                            </div>
                            <div className="col-lg-6 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="product_title"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Staffing Level
                                </label>
                                <select
                                  className="form-control"
                                  name="staffingLevel"
                                  value={formData.staffingLevel}
                                  onChange={handleChange}
                                >
                                  <option value="">
                                    Select staffing level
                                  </option>
                                  <option value="High">High</option>
                                  <option value="Adequate">Adequate</option>
                                  <option value="Inadequate">Inadequate</option>
                                </select>
                              </div>
                            </div>
                            <div className="col-lg-6 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="product_title"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Dispensed By
                                </label>
                                <input
                                  type="text"
                                  name="despensedBy"
                                  placeholder="Type here"
                                  className="form-control"
                                  value={formData.despensedBy}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>
                            <div className="col-lg-6 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="product_title"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Checked By
                                </label>
                                <input
                                  type="text"
                                  name="checkedBy"
                                  placeholder="Type here"
                                  className="form-control"
                                  value={formData.checkedBy}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>

                            {/* Drug Information Group - 4 inputs in a row */}
                            <div className="row">
                              <div className="col-lg-3 col-sm-6">
                                <div className="mb-8">
                                  <label
                                    htmlFor="drugName"
                                    className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                  >
                                    Drug Name
                                  </label>
                                  <input
                                    type="text"
                                    name="drugName"
                                    placeholder="Type here"
                                    className="form-control"
                                    value={formData.drugName}
                                    onChange={handleChange}
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-sm-6">
                                <div className="mb-8">
                                  <label
                                    htmlFor="drugBrand"
                                    className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                  >
                                    Brand
                                  </label>
                                  <input
                                    type="text"
                                    name="drugBrand"
                                    placeholder="Type here"
                                    className="form-control"
                                    value={formData.drugBrand}
                                    onChange={handleChange}
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-sm-6">
                                <div className="mb-8">
                                  <label
                                    htmlFor="drugStrength"
                                    className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                  >
                                    Strength
                                  </label>
                                  <input
                                    type="text"
                                    name="drugStrength"
                                    placeholder="Type here"
                                    className="form-control"
                                    value={formData.drugStrength}
                                    onChange={handleChange}
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-sm-6">
                                <div className="mb-8">
                                  <label
                                    htmlFor="drugForm"
                                    className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                  >
                                    Form
                                  </label>
                                  <input
                                    type="text"
                                    name="drugForm"
                                    placeholder="Type here"
                                    className="form-control"
                                    value={formData.drugForm}
                                    onChange={handleChange}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="col-lg-6 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="type"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Type of Incident
                                </label>
                                <select
                                  name="type"
                                  id="type"
                                  className="form-control"
                                  value={formData.type}
                                  onChange={handleChange}
                                >
                                  <option value="">Select Type</option>
                                  {incidentTypes.map((type, index) => (
                                    <option key={index} value={type}>
                                      {type}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Other Type Textarea - Conditionally Rendered */}
                            {showOtherType && (
                              <div className="col-lg-6 col-sm-12">
                                <div className="mb-8">
                                  <label
                                    htmlFor="otherType"
                                    className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                  >
                                    Specify Other Type
                                  </label>
                                  <TextArea
                                    name="otherType"
                                    placeholder="Please specify the incident type..."
                                    className="form-control"
                                    rows={4}
                                    value={formData.otherType}
                                    onChange={handleChange}
                                  />
                                </div>
                              </div>
                            )}

                            <div className="col-lg-6 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="cause"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Possible Causes
                                </label>
                                <select
                                  name="cause"
                                  id="cause"
                                  className="form-control"
                                  value={formData.cause}
                                  onChange={(e) =>
                                    handleChange({ target: { name: "cause", value: e.target.value } })
                                  }
                                >
                                  {possibleCauses.map((cause, index) => (
                                    <option key={index} value={cause}>
                                      {cause}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <div className="col-lg-12 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="product_title"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Things to consider
                                </label>
                                <ReactQuill
                                  theme="snow"
                                  value={formData.things}
                                  onChange={handleThingsChange}
                                />
                              </div>
                            </div>

                            <div className="col-lg-12 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="product_title"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Action Taken
                                </label>
                                <ReactQuill
                                  theme="snow"
                                  value={formData.action}
                                  onChange={handleActionChange}
                                />
                              </div>
                            </div>

                            <div className="col-lg-12 col-sm-12">
                              <div className="mb-8">
                                <label
                                  htmlFor="product_title"
                                  className="mb-4 fs-13px ls-1 fw-bold text-uppercase"
                                >
                                  Potential Adverse Event Discussion
                                </label>
                                <ReactQuill
                                  theme="snow"
                                  value={formData.discussion}
                                  onChange={handleDiscussionChange}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="col-sm-12 d-flex flex-wrap justify-content-sm-end">
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={isLoading}
                            >
                              {isLoading && (
                                <i className="fa fa-spinner fa-spin"></i>
                              )}{" "}
                              Update
                            </button>
                          </div>
                        </form>
                      )}
                      {!isDataLoading && isNotFound && (
                        <div className="text-center">
                          <img
                            src="/assets/images/no-data.png"
                            style={{ width: "60px", opacity: "0.5" }}
                            alt=""
                          />
                          <h6
                            style={{
                              color: "#918f8f",
                              fontFamily: "math",
                              fontWeight: "500",
                            }}
                          >
                            No Data Found
                          </h6>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default IncidentLogEdit;
